import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environmentURL } from 'src/environments/environments';
import {
  SocioOperacionItem,
  SocioOperacionPreview,
  SocioOperacionResultado,
  SocioOperacionEstatus,
} from '../models/administracion-socios.models';

interface ApiResponse<T> {
  ok: boolean;
  datos?: T;
  error?: string;
  operacion_id?: number;
  tipo?: string;
  estatus?: string;
}

@Injectable({ providedIn: 'root' })
export class AdministracionSociosService {
  private readonly baseUrl = environmentURL.apiBackendUrl;
  private readonly endpoint = `${this.baseUrl}socios/operacion`;
  private readonly httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
  };

  constructor(private http: HttpClient) {}

  /** Nombre del usuario en sesión para auditoría */
  private usuarioNombre(): string {
    const nombre = (localStorage.getItem('nombre') ?? '').trim();
    return nombre.length > 200 ? nombre.slice(0, 200) : nombre;
  }

  /** Id del usuario en sesión (opcional para backend) */
  private usuarioId(): number | undefined {
    const raw = localStorage.getItem('usuario_id');
    if (!raw) {
      return undefined;
    }
    const id = Number(raw);
    return Number.isFinite(id) ? id : undefined;
  }

  /** GET preview de impacto y checklist de fusión */
  obtenerPreview(maestroId: number, destinoId?: number | null): Observable<SocioOperacionPreview> {
    let params = new HttpParams()
      .set('accion', 'preview')
      .set('maestro_id', String(maestroId));
    if (destinoId != null) {
      params = params.set('destino_id', String(destinoId));
    }
    return this.http
      .get<ApiResponse<SocioOperacionPreview>>(this.endpoint, {
        ...this.httpOptions,
        params,
      })
      .pipe(
        map((r) => this.exigirDatos(r)),
        catchError(this.manejarError),
      );
  }

  /** POST eliminación física del socio origen */
  eliminar(maestroId: number, motivo: string): Observable<SocioOperacionResultado> {
    return this.http
      .post<ApiResponse<SocioOperacionResultado>>(
        this.endpoint,
        {
          accion: 'eliminar',
          maestro_id: maestroId,
          motivo,
          usuario_id: this.usuarioId(),
          usuario_nombre: this.usuarioNombre(),
        },
        this.httpOptions,
      )
      .pipe(
        map((r) => this.exigirResultado(r)),
        catchError(this.manejarError),
      );
  }

  /** POST fusión selectiva + eliminación del origen */
  eliminarMigrar(
    maestroId: number,
    destinoId: number,
    motivo: string,
    gruposMigrar: string[],
  ): Observable<SocioOperacionResultado> {
    return this.http
      .post<ApiResponse<SocioOperacionResultado>>(
        this.endpoint,
        {
          accion: 'eliminar-migrar',
          maestro_id: maestroId,
          destino_id: destinoId,
          motivo,
          grupos_migrar: gruposMigrar,
          usuario_id: this.usuarioId(),
          usuario_nombre: this.usuarioNombre(),
        },
        this.httpOptions,
      )
      .pipe(
        map((r) => this.exigirResultado(r)),
        catchError(this.manejarError),
      );
  }

  /** GET historial de operaciones para tabs 2 y 3 */
  listarOperaciones(): Observable<SocioOperacionItem[]> {
    const params = new HttpParams().set('accion', 'listar');
    return this.http
      .get<ApiResponse<SocioOperacionItem[]>>(this.endpoint, {
        ...this.httpOptions,
        params,
      })
      .pipe(
        map((r) => this.exigirDatos(r) ?? []),
        catchError(this.manejarError),
      );
  }

  /** POST restaurar operación aplicada (una sola vez) */
  restaurar(operacionId: number, motivo: string): Observable<SocioOperacionResultado> {
    return this.http
      .post<ApiResponse<SocioOperacionResultado>>(
        this.endpoint,
        {
          accion: 'restaurar',
          operacion_id: operacionId,
          motivo,
          usuario_id: this.usuarioId(),
          usuario_nombre: this.usuarioNombre(),
        },
        this.httpOptions,
      )
      .pipe(
        map((r) => this.exigirResultado(r)),
        catchError(this.manejarError),
      );
  }

  /** GET jobs EN_PROCESO y recién cerrados (chip / polling global) */
  estatusOperacion(operacionId?: number): Observable<SocioOperacionEstatus[]> {
    let params = new HttpParams().set('accion', 'estatus_operacion');
    const uid = this.usuarioId();
    if (uid != null) {
      params = params.set('usuario_id', String(uid));
    }
    if (operacionId != null) {
      params = params.set('operacion_id', String(operacionId));
    }
    return this.http
      .get<ApiResponse<SocioOperacionEstatus[]>>(this.endpoint, {
        ...this.httpOptions,
        params,
      })
      .pipe(
        map((r) => this.exigirDatos(r) ?? []),
        catchError(this.manejarError),
      );
  }

  /** GET PDF on-demand vía endpoint estándar de reportes */
  descargarReporteAuditoria(operacionId: number): Observable<Blob> {
    const params = new HttpParams()
      .set('reporte', 'SocioOperacionAuditoria')
      .set('id', String(operacionId));
    return this.http
      .get(`${this.baseUrl}reporte`, {
        params,
        responseType: 'blob',
      })
      .pipe(catchError(this.manejarError));
  }

  /** Extrae datos de respuesta API o lanza error legible */
  private exigirDatos<T>(resp: ApiResponse<T>): T {
    if (!resp?.ok) {
      throw new Error(resp?.error ?? 'Error en servicio de administración de socios');
    }
    return resp.datos as T;
  }

  private exigirResultado(resp: ApiResponse<SocioOperacionResultado>): SocioOperacionResultado {
    if (!resp?.ok) {
      throw new Error(resp?.error ?? 'Error en servicio de administración de socios');
    }
    if (resp.datos) {
      return resp.datos;
    }
    if (resp.operacion_id) {
      return {
        operacion_id: resp.operacion_id,
        tipo: resp.tipo,
        estatus: resp.estatus || 'EN_PROCESO',
      };
    }
    throw new Error('Respuesta de operación de socio incompleta');
  }

  private manejarError = (error: unknown): Observable<never> => {
    const err = error as {
      error?: { error?: string; mensaje?: string } | string | Blob;
      message?: string;
      status?: number;
      statusText?: string;
    };
    let msg = 'Error en servicio de administración de socios';
    if (error instanceof Error && error.message && !error.message.startsWith('Http failure')) {
      msg = error.message;
    } else if (err?.error instanceof Blob) {
      msg = 'No se pudo obtener el PDF';
    } else if (typeof err?.error === 'string' && err.error.trim()) {
      msg = err.error;
    } else if (typeof err?.error === 'object' && err.error) {
      msg = err.error.error ?? err.error.mensaje ?? msg;
    } else if (err?.message) {
      msg = err.message;
    }
    if ((!msg || msg === 'Error en servicio de administración de socios') && err?.status) {
      msg = `Error HTTP ${err.status}${err.statusText ? ` (${err.statusText})` : ''}`;
    }
    return throwError(() => new Error(msg));
  };
}
